/**
 * Timezone utilities — Phase 17 Tier-2 §2.1
 *
 * Per-recipient timezone arithmetic for:
 *   - alertEmailDispatcher.js quiet-hours check (was using browser's
 *     getHours; should use recipient's local hour per their IANA tz)
 *   - daily-digest cron (Phase 17 Tier-2 §2.2; fires at 8-10am SPL-local)
 *   - dispute escalation cron (Phase 17 Tier-2 §2.3; business-hours
 *     calculation per per-org SPL timezone)
 *
 * IANA timezone reference: https://en.wikipedia.org/wiki/List_of_tz_database_time_zones
 *
 * Browser support: Intl.DateTimeFormat is universal in 2026 (Chrome 24+,
 * Safari 10+, Firefox 29+). All Juuni's customer-supported browsers honor
 * Intl APIs without polyfills.
 */

/**
 * The default timezone when none specified. Matches migration 253 column
 * default + migration 288 trigger fallback.
 */
export const DEFAULT_TIMEZONE = 'UTC';

/**
 * Common timezone presets for dropdown selection. Subset of full IANA list
 * focused on B2B SaaS markets (US + EU + Asia-Pacific top 10).
 *
 * For full IANA list with all 600+ timezones, query Postgres
 * `SELECT name FROM pg_timezone_names ORDER BY name` server-side.
 */
export const COMMON_TIMEZONES = [
  // UTC + GMT
  { value: 'UTC', label: 'UTC (Coordinated Universal Time)', region: 'Global' },

  // North America (most-common B2B SaaS markets)
  { value: 'America/New_York', label: 'Eastern Time — US (New York)', region: 'North America' },
  { value: 'America/Chicago', label: 'Central Time — US (Chicago)', region: 'North America' },
  { value: 'America/Denver', label: 'Mountain Time — US (Denver)', region: 'North America' },
  { value: 'America/Los_Angeles', label: 'Pacific Time — US (Los Angeles)', region: 'North America' },
  { value: 'America/Phoenix', label: 'Arizona — US (Phoenix; no DST)', region: 'North America' },
  { value: 'America/Anchorage', label: 'Alaska Time — US (Anchorage)', region: 'North America' },
  { value: 'Pacific/Honolulu', label: 'Hawaii — US (Honolulu)', region: 'North America' },
  { value: 'America/Toronto', label: 'Eastern Time — Canada (Toronto)', region: 'North America' },
  { value: 'America/Vancouver', label: 'Pacific Time — Canada (Vancouver)', region: 'North America' },

  // Europe (G7 + key SaaS markets)
  { value: 'Europe/London', label: 'British Time — UK (London)', region: 'Europe' },
  { value: 'Europe/Dublin', label: 'Irish Time — Ireland (Dublin)', region: 'Europe' },
  { value: 'Europe/Paris', label: 'Central European Time — France (Paris)', region: 'Europe' },
  { value: 'Europe/Berlin', label: 'Central European Time — Germany (Berlin)', region: 'Europe' },
  { value: 'Europe/Amsterdam', label: 'Central European Time — Netherlands (Amsterdam)', region: 'Europe' },
  { value: 'Europe/Madrid', label: 'Central European Time — Spain (Madrid)', region: 'Europe' },
  { value: 'Europe/Stockholm', label: 'Central European Time — Sweden (Stockholm)', region: 'Europe' },

  // Asia-Pacific (key annotation/data-services markets)
  { value: 'Asia/Tokyo', label: 'Japan Standard Time — Japan (Tokyo)', region: 'Asia-Pacific' },
  { value: 'Asia/Seoul', label: 'Korea Standard Time — Korea (Seoul)', region: 'Asia-Pacific' },
  { value: 'Asia/Shanghai', label: 'China Standard Time — China (Shanghai)', region: 'Asia-Pacific' },
  { value: 'Asia/Hong_Kong', label: 'Hong Kong Time (Hong Kong)', region: 'Asia-Pacific' },
  { value: 'Asia/Singapore', label: 'Singapore Standard Time (Singapore)', region: 'Asia-Pacific' },
  { value: 'Asia/Kolkata', label: 'India Standard Time — India (Kolkata)', region: 'Asia-Pacific' },
  { value: 'Asia/Manila', label: 'Philippine Time — Philippines (Manila)', region: 'Asia-Pacific' },
  { value: 'Australia/Sydney', label: 'Eastern Australia Time (Sydney)', region: 'Asia-Pacific' },
  { value: 'Australia/Melbourne', label: 'Eastern Australia Time (Melbourne)', region: 'Asia-Pacific' },

  // South America (emerging annotation markets)
  { value: 'America/Sao_Paulo', label: 'Brasília Time — Brazil (São Paulo)', region: 'South America' },
  { value: 'America/Buenos_Aires', label: 'Argentina Time (Buenos Aires)', region: 'South America' },
  { value: 'America/Mexico_City', label: 'Central Time — Mexico (Mexico City)', region: 'South America' },
];

/**
 * Verify a string is a valid IANA timezone identifier.
 * Returns true if the timezone is recognized by Intl.DateTimeFormat.
 *
 * Server-side validation lives in migration 288 trigger
 * (validate_individual_timezone) which checks against pg_timezone_names.
 * Client-side uses this for pre-submit validation.
 *
 * @param {string} tz - timezone identifier (e.g., 'America/New_York')
 * @returns {boolean}
 */
export function isValidTimezone(tz) {
  if (!tz || typeof tz !== 'string' || tz.trim() === '') {
    return false;
  }

  try {
    // Intl.DateTimeFormat throws RangeError on invalid timezone
    new Intl.DateTimeFormat('en-US', { timeZone: tz });
    return true;
  } catch (err) {
    if (err instanceof RangeError) {
      return false;
    }
    // Re-throw unexpected errors (e.g., environment without Intl)
    throw err;
  }
}

/**
 * Get the current hour (0-23) in the specified timezone.
 *
 * Used by alertEmailDispatcher quiet-hours check. Was previously using
 * `now.getHours()` (browser local) which is the bug Phase 17 Tier-2
 * adversarial review identified.
 *
 * @param {string} tz - IANA timezone (e.g., 'America/New_York')
 * @param {Date} [now] - injection point for testing (defaults to current time)
 * @returns {number} hour in 0-23 range
 */
export function getHourInTimezone(tz, now = new Date()) {
  if (!isValidTimezone(tz)) {
    // Fallback to UTC on invalid input rather than crashing
    tz = DEFAULT_TIMEZONE;
  }

  // Intl.DateTimeFormat with hour12=false returns 24-hour format
  const formatter = new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    hourCycle: 'h23',
    timeZone: tz,
  });

  const parts = formatter.formatToParts(now);
  const hourPart = parts.find((p) => p.type === 'hour');

  if (!hourPart) return 0;

  const hour = parseInt(hourPart.value, 10);
  return Number.isFinite(hour) ? hour : 0;
}

/**
 * Determine if "now" is in the recipient's quiet hours (10pm-7am local).
 *
 * Replaces the bugged `now.getHours()` check in alertEmailDispatcher:
 *   const hour = now.getHours();  // BAD: browser's local time
 *   return hour >= 22 || hour < 7;
 *
 * Now:
 *   return isQuietHoursForRecipient('America/Los_Angeles', now);
 *   // Correct: 22:00-07:00 in PST regardless of dispatcher's runtime tz
 *
 * @param {string} tz - recipient's IANA timezone
 * @param {Date} [now] - injection point for testing
 * @returns {boolean}
 */
export function isQuietHoursForRecipient(tz, now = new Date()) {
  const hour = getHourInTimezone(tz, now);
  return hour >= 22 || hour < 7;
}

/**
 * Check if "now" falls within a recipient's daily-digest delivery window.
 * Default window: 8-10am local (most B2B users start their morning).
 *
 * Used by daily-digest cron (Phase 17 Tier-2 §2.2) to determine which
 * SPLs should receive a digest on this cron tick.
 *
 * @param {string} tz - recipient's IANA timezone
 * @param {Date} [now] - injection point for testing
 * @param {Object} [options]
 * @param {number} [options.startHour=8] - inclusive start hour (default 8am)
 * @param {number} [options.endHour=10] - exclusive end hour (default 10am)
 * @returns {boolean}
 */
export function isInDigestWindow(tz, now = new Date(), { startHour = 8, endHour = 10 } = {}) {
  const hour = getHourInTimezone(tz, now);
  return hour >= startHour && hour < endHour;
}

/**
 * Format a date for display in a specific timezone.
 * Useful for emails, logs, and UI that show times.
 *
 * @param {Date|string} date - the date to format
 * @param {string} tz - IANA timezone
 * @param {Object} [options] - Intl.DateTimeFormat options (default: friendly)
 * @returns {string} formatted date string
 */
export function formatDateInTimezone(
  date,
  tz,
  options = {
    dateStyle: 'medium',
    timeStyle: 'short',
  }
) {
  if (!isValidTimezone(tz)) tz = DEFAULT_TIMEZONE;

  const dateObj = typeof date === 'string' ? new Date(date) : date;
  return new Intl.DateTimeFormat('en-US', { ...options, timeZone: tz }).format(dateObj);
}

/**
 * Detect the user's browser timezone via Intl API.
 * Used during signup to suggest a default; user can override.
 *
 * @returns {string} IANA timezone (e.g., 'America/New_York'); 'UTC' fallback
 */
export function detectBrowserTimezone() {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return isValidTimezone(tz) ? tz : DEFAULT_TIMEZONE;
  } catch {
    // Pre-Intl environment or rare runtime errors; return safe default
    return DEFAULT_TIMEZONE;
  }
}

/**
 * Get timezones grouped by region for use in dropdown UI.
 * @returns {Object} { region: [{ value, label }, ...] }
 */
export function getTimezonesByRegion() {
  return COMMON_TIMEZONES.reduce((acc, tz) => {
    if (!acc[tz.region]) acc[tz.region] = [];
    acc[tz.region].push({ value: tz.value, label: tz.label });
    return acc;
  }, {});
}
